import React, { useEffect, useMemo, useState } from 'react';
import { initMercadoPago, CardPayment } from '@mercadopago/sdk-react';
import { mercadoPagoService } from '../../services/mercadoPagoService';
import { notifications } from '@mantine/notifications';

interface Props {
  planId: number;
  amount: number;
  user: { id: number; email: string; nombre?: string };
  couponCode?: string;
  savePaymentMethod?: boolean;
  onSuccess: (data: { paymentId: string; status: string; subscriptionId: number }) => void;
  onError: (message: string) => void;
}

const MercadoPagoCardForm: React.FC<Props> = ({ planId, amount, user, couponCode, savePaymentMethod = false, onSuccess, onError }) => {
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await mercadoPagoService.getConfig();
        if (!cfg?.publicKey) {
          onError('MercadoPago no está configurado');
          return;
        }
        setPublicKey(cfg.publicKey);
        initMercadoPago(cfg.publicKey, { 
          locale: 'es-MX'
        });
        
        // Dar tiempo para que se inicialice completamente
        setTimeout(() => setIsReady(true), 1000);
      } catch (error) {
        console.error('Error initializing MercadoPago:', error);
        onError('Error al inicializar MercadoPago');
      }
    })();
  }, [onError]);

  const initialization = useMemo(() => ({ amount }), [amount]);

  if (!publicKey || !isReady) return <div>Cargando formulario de pago...</div>;

  return (
    <CardPayment
      initialization={initialization}
      onSubmit={async (formData: any) => {
        try {
          const result = await mercadoPagoService.directPayment({
            userId: user.id,
            planId,
            token: formData.token,
            payerEmail: user.email,
            payerFirstName: user.nombre,
            installments: formData.installments || 1,
            paymentMethodId: formData.payment_method_id,
            issuerId: formData.issuer_id,
            description: `Suscripción plan ${planId}`,
            externalReference: `SUB_${user.id}_${planId}_${Date.now()}`,
            couponCode,
            savePaymentMethod: savePaymentMethod,
            metadata: { ui: 'card_payment_brick' },
            transactionAmount: amount,
          });

          if (result?.success) {
            const { payment } = result;
            
            // Mostrar mensaje si el método de pago fue guardado
            if (savePaymentMethod && payment.status === 'approved') {
              notifications.show({
                color: 'green',
                title: 'Método de pago guardado',
                message: 'Tu tarjeta se guardó exitosamente para futuras compras',
              });
            }
            
            // Guardar información del pago para la página de éxito
            const paymentData = {
              paymentId: String(payment.paymentId),
              status: payment.status,
              subscriptionId: Number(payment.subscriptionId),
              planId,
              amount,
              timestamp: new Date().toISOString()
            };
            sessionStorage.setItem('payment_success_data', JSON.stringify(paymentData));
            
            onSuccess(paymentData);
          } else {
            onError('No se pudo procesar el pago');
          }
        } catch (e: any) {
          onError(e?.response?.data?.message || e?.message || 'Error procesando pago');
        }
      }}
      onError={(error: any) => {
        console.error('CardPayment error:', error);
        onError(error?.message || 'Error en el formulario de pago');
      }}
      customization={{
        visual: {
          style: {
            theme: 'bootstrap',
            customVariables: {
              formBackgroundColor: '#ffffff',
              inputBackgroundColor: '#ffffff',
              baseColor: '#3b82f6',
            },
          },
        },
      }}
    />
  );
};

export default MercadoPagoCardForm;